/**
 * VoiceAgentWebSocket - Browser voice agent for Gemini Live backend
 *
 * Connects to the bridge's WebSocket endpoint for real-time audio streaming.
 * Captures mic audio at 24kHz PCM int16, sends as ArrayBuffers.
 * Receives PCM audio and JSON messages from the bridge.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'

import type { Message, SecurityBadge } from './lib/types'
import { generateMessageId } from './lib/utils'

interface VoiceAgentWebSocketProps {
  agentId: string
  agentName?: string
  bridgeUrl: string
  bridgeType?: 'gemini_live' | 'xai_realtime'
  voiceId?: string
  instructions?: string
  language?: string
  onClose?: () => void
  onError?: (error: Error) => void
  autoConnect?: boolean
  demoMode?: boolean
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

const SAMPLE_RATE = 24000
const BUFFER_SIZE = 4096

export const VoiceAgentWebSocket: React.FC<VoiceAgentWebSocketProps> = ({
  agentId,
  agentName,
  bridgeUrl,
  bridgeType = 'gemini_live',
  voiceId,
  instructions,
  language,
  onClose,
  onError,
  autoConnect = false,
  demoMode = false,
}) => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected')
  const [isCallActive, setIsCallActive] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [transcript, setTranscript] = useState<Message[]>([])
  const [agentState, setAgentState] = useState<string>('idle')
  const [error, setError] = useState<string | null>(null)
  const [textInput, setTextInput] = useState('')

  // Demo mode: track conversation turns to apply scripted security badges
  const demoTurnRef = useRef(0)
  const demoLastRoleRef = useRef<string>('')

  const wsRef = useRef<WebSocket | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null)
  const playbackContextRef = useRef<AudioContext | null>(null)
  const playbackQueueRef = useRef<Float32Array[]>([])
  const isPlayingRef = useRef(false)
  // Tracks every AudioBufferSourceNode currently playing so a barge-in can
  // stop them all instantly. Without this, sources start with no handle and
  // run to completion regardless of cancel signals from the server.
  const activeSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set())
  const transcriptEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [transcript])

  const addTranscriptMessage = useCallback((role: 'user' | 'agent', content: string, stream = false, badges?: SecurityBadge[]) => {
    setTranscript((prev) => {
      const last = prev[prev.length - 1]
      // Append to the last bubble if streaming and same role
      if (stream && last && last.role === role) {
        const updated = [...prev]
        const mergedBadges = badges ? [...(last.securityBadges || []), ...badges] : last.securityBadges
        updated[updated.length - 1] = { ...last, content: last.content + content, securityBadges: mergedBadges }
        return updated
      }
      return [
        ...prev,
        {
          id: generateMessageId(),
          role,
          content,
          timestamp: new Date(),
          type: 'voice' as const,
          securityBadges: badges,
        },
      ]
    })
  }, [])

  const drainPlaybackQueue = useCallback(() => {
    const ctx = playbackContextRef.current
    if (!ctx || playbackQueueRef.current.length === 0) {
      isPlayingRef.current = false
      return
    }

    isPlayingRef.current = true
    const chunk = playbackQueueRef.current.shift()!

    const buffer = ctx.createBuffer(1, chunk.length, SAMPLE_RATE)
    buffer.getChannelData(0).set(chunk)

    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(ctx.destination)
    activeSourcesRef.current.add(source)
    source.onended = () => {
      activeSourcesRef.current.delete(source)
      drainPlaybackQueue()
    }
    source.start()
  }, [])

  /**
   * Stop everything currently playing or queued. Called on barge_in from the
   * server (user started speaking over the agent) and on disconnect.
   *
   * Order matters: clear the queue first, null out onended on each source so
   * stopping doesn't kick off another drain, then stop and disconnect.
   */
  const flushPlayback = useCallback(() => {
    playbackQueueRef.current = []
    for (const source of activeSourcesRef.current) {
      source.onended = null
      try {
        source.stop()
      } catch {
        /* already stopped */
      }
      try {
        source.disconnect()
      } catch {
        /* already disconnected */
      }
    }
    activeSourcesRef.current.clear()
    isPlayingRef.current = false
  }, [])

  /**
   * Play received PCM audio through speakers
   */
  const playAudioChunk = useCallback(
    (pcmData: Int16Array) => {
      if (!playbackContextRef.current) {
        playbackContextRef.current = new AudioContext({ sampleRate: SAMPLE_RATE })
      }

      // Convert Int16 to Float32
      const float32 = new Float32Array(pcmData.length)
      for (let i = 0; i < pcmData.length; i++) {
        float32[i] = pcmData[i] / 32768
      }

      playbackQueueRef.current.push(float32)

      if (!isPlayingRef.current) {
        drainPlaybackQueue()
      }
    },
    [drainPlaybackQueue],
  )

  /**
   * Start capturing microphone audio
   */
  const startMicrophone = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })

      mediaStreamRef.current = stream

      const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE })
      audioContextRef.current = audioContext

      const source = audioContext.createMediaStreamSource(stream)
      const processor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1)
      scriptProcessorRef.current = processor

      processor.onaudioprocess = (event) => {
        if (isMuted || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return

        const inputData = event.inputBuffer.getChannelData(0)
        // Convert Float32 to Int16
        const int16 = new Int16Array(inputData.length)
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]))
          int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
        }

        wsRef.current.send(int16.buffer)
      }

      source.connect(processor)
      processor.connect(audioContext.destination)
    } catch (err) {
      const error = err as Error
      console.error('[VoiceAgentWebSocket] Microphone access failed:', error)
      setError(`Microphone access failed: ${error.message}`)
      onError?.(error)
    }
  }, [isMuted, onError])

  /**
   * Stop microphone capture
   */
  const stopMicrophone = useCallback(() => {
    scriptProcessorRef.current?.disconnect()
    scriptProcessorRef.current = null

    mediaStreamRef.current?.getTracks().forEach((track) => track.stop())
    mediaStreamRef.current = null

    audioContextRef.current?.close()
    audioContextRef.current = null
  }, [])

  /**
   * Disconnect from bridge
   */
  const disconnect = useCallback(() => {
    wsRef.current?.close()
    wsRef.current = null
    stopMicrophone()
    flushPlayback()

    playbackContextRef.current?.close()
    playbackContextRef.current = null

    setConnectionStatus('disconnected')
    setIsCallActive(false)
  }, [stopMicrophone, flushPlayback])

  // Demo mode badge helpers
  const getDemoUserBadges = useCallback(
    (turn: number): SecurityBadge[] | undefined => {
      if (!demoMode) return undefined
      if (turn === 2) return [{ type: 'ai_detected', label: 'AI Caller Detected', color: 'red' }]
      return undefined
    },
    [demoMode],
  )

  const getDemoAgentBadges = useCallback(
    (turn: number): SecurityBadge[] | undefined => {
      if (!demoMode) return undefined
      const watermark: SecurityBadge = { type: 'watermarked', label: 'Watermarked', color: 'green' }
      if (turn === 2) return [watermark, { type: 'efficient_mode', label: 'Efficient mode', color: 'blue' }]
      if (turn === 3) return [watermark, { type: 'pii_blocked', label: 'PII request blocked', color: 'amber' }]
      return [watermark]
    },
    [demoMode],
  )

  /**
   * Handle JSON messages from the bridge
   */
  const handleBridgeMessage = useCallback(
    (msg: any) => {
      switch (msg.type) {
        case 'transcript':
          if (msg.role && msg.text) {
            if (msg.role === 'user') {
              if (demoMode && demoLastRoleRef.current !== 'user') demoTurnRef.current++
              demoLastRoleRef.current = 'user'
              addTranscriptMessage('user', msg.text, true, getDemoUserBadges(demoTurnRef.current))
            } else {
              demoLastRoleRef.current = 'agent'
              addTranscriptMessage('agent', msg.text, true, getDemoAgentBadges(demoTurnRef.current))
            }
          }
          break
        // xAI bridge message types
        case 'agent_transcript':
          if (msg.delta) {
            demoLastRoleRef.current = 'agent'
            addTranscriptMessage('agent', msg.delta, true, getDemoAgentBadges(demoTurnRef.current))
          }
          break
        case 'user_transcript':
          if (msg.delta) {
            if (demoMode && demoLastRoleRef.current !== 'user') demoTurnRef.current++
            demoLastRoleRef.current = 'user'
            addTranscriptMessage('user', msg.delta, true, getDemoUserBadges(demoTurnRef.current))
          }
          break
        case 'response_done':
          // xAI response complete, could log usage
          console.log('[VoiceAgentWebSocket] xAI response done:', msg.usage)
          break
        case 'barge_in':
          // Server-side VAD detected the user speaking over the agent. Stop
          // every scheduled audio chunk immediately so the user isn't fighting
          // playback while the agent generates its next response.
          flushPlayback()
          break
        case 'agent_state':
          setAgentState(msg.state || 'idle')
          break
        case 'error':
          setError(msg.message || 'Unknown error from bridge')
          break
        case 'call_ended':
          disconnect()
          break
        default:
          console.log('[VoiceAgentWebSocket] Unknown message type:', msg.type)
      }
    },
    [addTranscriptMessage, demoMode, getDemoUserBadges, getDemoAgentBadges, disconnect, flushPlayback],
  )

  /**
   * Connect to bridge WebSocket
   */
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return

    setConnectionStatus('connecting')
    setError(null)

    // Build WebSocket URL — keep it short, send config via message after connect
    let wsUrl: string
    const wsBase = bridgeUrl.replace(/^https?:\/\//, 'wss://')
    if (bridgeType === 'xai_realtime') {
      wsUrl = `${wsBase}/ws/browser`
    } else {
      wsUrl = `${wsBase}/ws/browser/${agentId}`
    }

    const ws = new WebSocket(wsUrl)
    ws.binaryType = 'arraybuffer'
    wsRef.current = ws

    ws.onopen = () => {
      console.log('[VoiceAgentWebSocket] Connected')
      // Send config message with full payload (avoids URL length limits for long prompts)
      if (bridgeType === 'xai_realtime') {
        const configMsg: Record<string, string> = { type: 'config' }
        if (voiceId) configMsg.voice_id = voiceId
        if (instructions) configMsg.instructions = instructions
        if (language) configMsg.language = language
        ws.send(JSON.stringify(configMsg))
      } else {
        const configMsg: Record<string, string> = { type: 'config' }
        if (instructions) configMsg.system_prompt = instructions
        if (voiceId) configMsg.voice_uuid = voiceId
        if (language) configMsg.language = language
        ws.send(JSON.stringify(configMsg))
      }
      setConnectionStatus('connected')
      setIsCallActive(true)
      startMicrophone()
    }

    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        // Binary data = PCM audio from the agent
        const pcmData = new Int16Array(event.data)
        playAudioChunk(pcmData)
      } else {
        // JSON message (transcript, state updates, etc.)
        try {
          const msg = JSON.parse(event.data)
          handleBridgeMessage(msg)
        } catch (err) {
          console.warn('[VoiceAgentWebSocket] Failed to parse message:', event.data)
        }
      }
    }

    ws.onclose = (event) => {
      console.log('[VoiceAgentWebSocket] Disconnected:', event.code, event.reason)
      setConnectionStatus('disconnected')
      setIsCallActive(false)
      stopMicrophone()
    }

    ws.onerror = (event) => {
      console.error('[VoiceAgentWebSocket] Error:', event)
      setConnectionStatus('error')
      setError('WebSocket connection failed')
      onError?.(new Error('WebSocket connection failed'))
    }
  }, [agentId, bridgeUrl, bridgeType, voiceId, instructions, language, startMicrophone, stopMicrophone, playAudioChunk, handleBridgeMessage, onError])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])

  // Auto-connect if specified
  useEffect(() => {
    if (autoConnect) {
      connect()
    }
  }, [autoConnect, connect])

  const toggleMute = () => setIsMuted((prev) => !prev)

  const sendTextMessage = useCallback(() => {
    const text = textInput.trim()
    if (!text || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return

    wsRef.current.send(JSON.stringify({ type: 'text', text }))
    addTranscriptMessage('user', text)
    setTextInput('')
  }, [textInput, addTranscriptMessage])

  return (
    <div className='tw-flex tw-h-full tw-flex-col tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg'>
      {/* Header */}
      <div className='tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-100 tw-px-5 tw-py-3'>
        <div className='tw-flex tw-items-center tw-gap-2'>
          <span className='tw-text-sm tw-font-semibold tw-text-gray-900'>{agentName || 'Voice Agent'}</span>
          <span
            className={`tw-inline-block tw-h-2 tw-w-2 tw-rounded-full ${
              connectionStatus === 'connected' ? 'tw-bg-green-500' : connectionStatus === 'connecting' ? 'tw-animate-pulse tw-bg-amber-500' : 'tw-bg-gray-300'
            }`}
          />
          <span className='tw-text-xs tw-text-gray-500'>{agentState !== 'idle' ? agentState : connectionStatus}</span>
        </div>
        {onClose && (
          <button
            type='button'
            onClick={() => {
              disconnect()
              onClose()
            }}
            className='tw-rounded-full tw-p-1 tw-text-gray-400 hover:tw-bg-gray-100 hover:tw-text-gray-600'
          >
            ✕
          </button>
        )}
      </div>

      {/* Transcript */}
      <div className='tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4' style={{ maxHeight: '400px' }}>
        {transcript.length === 0 && (
          <div className='tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm tw-text-gray-400'>{isCallActive ? 'Listening...' : 'Press Start to begin the conversation'}</div>
        )}
        {transcript.map((msg) => (
          <div key={msg.id} className={`tw-mb-3 tw-flex ${msg.role === 'user' ? 'tw-justify-end' : 'tw-justify-start'}`}>
            <div className='tw-max-w-[80%]'>
              <div className={`tw-rounded-2xl tw-px-4 tw-py-2 tw-text-sm ${msg.role === 'user' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-gray-100 tw-text-gray-900'}`}>{msg.content}</div>
              {msg.securityBadges && msg.securityBadges.length > 0 && (
                <div className={`tw-mt-1 tw-flex tw-flex-wrap tw-gap-1 ${msg.role === 'user' ? 'tw-justify-end' : 'tw-justify-start'}`}>
                  {/* Deduplicate badges by type */}
                  {[...new Map(msg.securityBadges.map((b) => [b.type, b])).values()].map((badge) => {
                    const colorMap = {
                      green: 'tw-bg-green-50 tw-text-green-700 tw-border-green-200',
                      red: 'tw-bg-red-50 tw-text-red-700 tw-border-red-200',
                      amber: 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200',
                      blue: 'tw-bg-blue-50 tw-text-blue-700 tw-border-blue-200',
                    }
                    const iconMap = {
                      watermarked: '\u2713',
                      ai_detected: '\u26A0',
                      efficient_mode: '\u26A1',
                      pii_blocked: '\u26D4',
                    }
                    return (
                      <span key={badge.type} className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-medium ${colorMap[badge.color]}`}>
                        {iconMap[badge.type]} {badge.label}
                      </span>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={transcriptEndRef} />
      </div>

      {/* Error */}
      {error && <div className='tw-mx-5 tw-mb-2 tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-px-3 tw-py-2 tw-text-xs tw-text-red-700'>{error}</div>}

      {/* Text Input */}
      {isCallActive && (
        <div className='tw-flex tw-items-center tw-gap-2 tw-px-5 tw-pb-2'>
          <input
            type='text'
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') sendTextMessage()
            }}
            placeholder='Type a message...'
            className='form-control tw-flex-1 tw-rounded-full tw-border tw-border-gray-300 tw-px-4 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-outline-none'
          />
          <button
            type='button'
            onClick={sendTextMessage}
            disabled={!textInput.trim()}
            className='tw-rounded-full tw-bg-blue-600 tw-p-2 tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-40'
            title='Send'
          >
            <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' className='tw-h-4 tw-w-4'>
              <path d='M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z' />
            </svg>
          </button>
        </div>
      )}

      {/* Controls */}
      <div className='tw-flex tw-items-center tw-justify-center tw-gap-4 tw-border-t tw-border-gray-100 tw-px-5 tw-py-4'>
        {!isCallActive ? (
          <button
            type='button'
            onClick={connect}
            disabled={connectionStatus === 'connecting'}
            className='tw-rounded-full tw-bg-green-600 tw-px-8 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm hover:tw-bg-green-700 disabled:tw-opacity-50'
          >
            {connectionStatus === 'connecting' ? 'Connecting...' : 'Start Call'}
          </button>
        ) : (
          <>
            <button
              type='button'
              onClick={toggleMute}
              className={`tw-rounded-full tw-p-3 tw-shadow-sm ${isMuted ? 'tw-bg-red-100 tw-text-red-600 hover:tw-bg-red-200' : 'tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-gray-200'}`}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? '🔇' : '🎙️'}
            </button>
            <button type='button' onClick={disconnect} className='tw-rounded-full tw-bg-red-600 tw-px-6 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm hover:tw-bg-red-700'>
              End Call
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default VoiceAgentWebSocket
